import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { CardComp, Loader } from "../../../components";
import { ModalCompAdvance } from "../../../components/Ui/ModalComp";
import Message from "../../../components/MessageModal";
import {
  useGetCooperativeLoansQuery,
  useGetCooperativeContributionsQuery,
  useGetCooperativeTransactionsQuery,
  useGetCooperativeStatisticsQuery,
  useGetCooperativesQuery,
  useUpdateCooperativeMutation,
  usePublishWebsiteRoutingMutation,
  useSendCooperativeCredentialsMutation,
  useResendWelcomeEmailMutation,
  useStartCooperativeSupportSessionMutation,
  useLazyProrateMemberSlotsQuery,
  useTopUpMemberSlotsMutation,
} from "../../../store/auth/authApi";
import { useUser } from "../../../hooks";
import { useGetCountriesQuery, useGetCitiesQuery } from "../../../store/selectableSlice";
import { formatMoney } from "../../../utils";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import { uploadImage } from "../../../utils/uploadImage";
import { Input } from "../../../components/Ui/new-input";
import CustomSelect from "../../../components/custom-select/custom-select";
import { customStyles } from "../../../components/custom-select/custom-styles";
import { FileUploaderComp } from "../../../components/Ui/FileUploader";
import ThemeBrandingEditor from "../../../components/Common/ThemeBrandingEditor";
import CooperativeModuleAssignmentCard from "../../../components/Admin/CooperativeModuleAssignmentCard";
import CooperativePaymentMethodsCard from "../../../components/Admin/CooperativePaymentMethodsCard";
import { DEFAULT_THEME, normalizeTheme, themeToApiPayload } from "../../../theme/companyTheme";
import {
  FaInfoCircle,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaGlobe,
  FaCalendarAlt,
  FaTag,
  FaHome,
  FaEdit,
  FaTimes,
  FaCheck,
  FaUserShield,
} from "react-icons/fa";

const MetricCard = ({ label, value, hint }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
      {label}
    </p>
    <p className="text-xl sm:text-2xl font-bold text-[#383838] break-words">
      {value}
    </p>
    {hint ? <p className="text-xs text-gray-400 mt-1">{hint}</p> : null}
  </div>
);

const formatListDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatListDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const DetailField = ({ label, children, full }) => (
  <div className={`${full ? "sm:col-span-2" : ""} border-b border-gray-100 pb-3`}>
    <p className="text-xs text-gray-500 mb-1">{label}</p>
    <div className="text-sm font-semibold text-[#383838] break-words">{children}</div>
  </div>
);

const ListPagination = ({ page, lastPage, total, perPage, onPageChange }) => {
  const safeLast = Math.max(1, Number(lastPage) || 1);
  const safePage = Math.min(Math.max(1, Number(page) || 1), safeLast);
  const totalCount = Number(total) || 0;
  if (totalCount === 0) return null;

  const from = (safePage - 1) * perPage + 1;
  const to = Math.min(safePage * perPage, totalCount);

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4 pt-4 border-t border-gray-200">
      <p className="text-sm text-gray-500">
        Showing {from}–{to} of {totalCount}
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={safePage <= 1}
          onClick={() => onPageChange(safePage - 1)}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          Previous
        </button>
        <span className="text-sm text-gray-600 px-2">
          Page {safePage} of {safeLast}
        </span>
        <button
          type="button"
          disabled={safePage >= safeLast}
          onClick={() => onPageChange(safePage + 1)}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          Next
        </button>
      </div>
    </div>
  );
};

const tableHeadClass =
  "text-left py-3 px-4 text-sm font-semibold text-white whitespace-nowrap";
const tableCellClass = "py-3 px-4 text-sm text-[#383838] border-b border-[#e0e0e0]";


const CooperativeDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [statisticsFilter, setStatisticsFilter] = useState("weekly");
  const [isEditMode, setIsEditMode] = useState(false);
  const [logoUrl, setLogoUrl] = useState("");
  const [logoPreview, setLogoPreview] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // 'credentials' or 'welcome'
  const [isSendingAction, setIsSendingAction] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [showAddSlotsModal, setShowAddSlotsModal] = useState(false);
  const [addSlotsCount, setAddSlotsCount] = useState(1);
  const [addSlotsDiscount, setAddSlotsDiscount] = useState("");
  const [prorateResult, setProrateResult] = useState(null);
  /** Edit gateway/convenience fee settings independently of the main cooperative form */
  const [isPaymentFeeEditMode, setIsPaymentFeeEditMode] = useState(false);
  const [contributionsPage, setContributionsPage] = useState(1);
  const [loansPage, setLoansPage] = useState(1);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const listPerPage = 10;

  // Fetch data for all tabs
  const { data: loansData, isLoading: loansLoading } =
    useGetCooperativeLoansQuery({
      companyId: id,
      page: loansPage,
      perPage: listPerPage,
      branchId: selectedBranch?.value,
    });

  const { data: contributionsData, isLoading: contributionsLoading } =
    useGetCooperativeContributionsQuery({
      companyId: id,
      page: contributionsPage,
      perPage: listPerPage,
      branchId: selectedBranch?.value,
    });

  const { data: transactionsData, isLoading: transactionsLoading } =
    useGetCooperativeTransactionsQuery({
      companyId: id,
      page: transactionsPage,
      perPage: listPerPage,
      branchId: selectedBranch?.value,
    });

  const { data: statisticsData, isLoading: statisticsLoading } =
    useGetCooperativeStatisticsQuery({
      companyId: id,
      filter: statisticsFilter,
      branchId: selectedBranch?.value,
    });

  const loans = Array.isArray(loansData?.data?.data) ? loansData.data.data : [];
  const contributions = Array.isArray(contributionsData?.data?.data) ? contributionsData.data.data : [];
  const transactions = Array.isArray(transactionsData?.data?.data) ? transactionsData.data.data : [];
  const statistics = Array.isArray(statisticsData?.data) ? statisticsData.data : [];

  const loansTotal = loansData?.total ?? loansData?.data?.total ?? loans.length;
  const contributionsTotal =
    contributionsData?.total ?? contributionsData?.data?.total ?? contributions.length;
  const transactionsTotal =
    transactionsData?.total ?? transactionsData?.data?.total ?? transactions.length;

  const loansLastPage =
    loansData?.lastPage ?? loansData?.data?.last_page ?? 1;
  const contributionsLastPage =
    contributionsData?.lastPage ?? contributionsData?.data?.last_page ?? 1;
  const transactionsLastPage =
    transactionsData?.lastPage ?? transactionsData?.data?.last_page ?? 1;

  useEffect(() => {
    setContributionsPage(1);
    setLoansPage(1);
    setTransactionsPage(1);
  }, [selectedBranch?.value]);

  const tabs = [
    { id: "overview", label: "Overview", icon: "📊" },
    { id: "details", label: "Details", icon: "ℹ️" },
    { id: "loans", label: "Loans", icon: "💰" },
    { id: "contributions", label: "Contributions", icon: "💳" },
    { id: "transactions", label: "Transactions", icon: "📋" },
    { id: "statistics", label: "Statistics", icon: "📈" },
  ];

  // Fetch cooperative details (branch-scoped when a branch is selected)
  const { 
    data: cooperativeData, 
    refetch: refetchCooperative,
    isLoading: isLoadingCooperative,
    isError: isErrorCooperative,
    error: cooperativeError
  } = useGetCooperativesQuery(
    { id, branchId: selectedBranch?.value || undefined },
    {
      skip: !id,
    }
  );

  // Fetch countries and cities for edit form
  const { data: countries = [], isLoading: isLoadingCountries } = useGetCountriesQuery();
  const { data: cities = [], isLoading: isLoadingCities } = useGetCitiesQuery();
  
  // Update mutation
  const [updateCooperative, { isLoading: isUpdating }] = useUpdateCooperativeMutation();
  const [publishWebsiteRouting, { isLoading: isPublishingRouting }] =
    usePublishWebsiteRoutingMutation();
  
  // Send credentials mutation
  const [sendCredentials] = useSendCooperativeCredentialsMutation();
  
  // Resend welcome email mutation
  const [resendWelcome] = useResendWelcomeEmailMutation();

  const managerUser = useUser();
  const canStartSupportSession =
    Number(managerUser?.role_id) === 1 ||
    String(managerUser?.role?.alias || "").toLowerCase() === "super_admin" ||
    String(managerUser?.role?.name || "")
      .toLowerCase()
      .includes("super admin");
  const [startSupportSession, { isLoading: isStartingSupport }] =
    useStartCooperativeSupportSessionMutation();

  // Add member slots (prorate + top-up)
  const [triggerProrate, { isLoading: prorateLoading }] = useLazyProrateMemberSlotsQuery();
  const [topUpMemberSlots, { isLoading: topUpLoading }] = useTopUpMemberSlotsMutation();

  const buildCooperativeUpdatePayload = (values) => ({
    name: values.name,
    phone: values.phone,
    country_id: parseInt(values.country_id),
    city_id: parseInt(values.city_id),
    address: values.address,
    logo_url:
      logoUrl ||
      values.logo_url ||
      "https://www.gravatar.com/avatar/a258389389f40731d3ac10839ec143a2?d=identicon&r=pg&s=100",
    gateway_fee_bearer: values.gateway_fee_bearer,
    convenience_fee_bearer: values.convenience_fee_bearer,
    convenience_fee_ngn:
      values.convenience_fee_ngn === "" || values.convenience_fee_ngn === null
        ? null
        : parseFloat(values.convenience_fee_ngn),
    ...themeToApiPayload({
      primary: values.primary_color,
      secondary: values.secondary_color,
    }),
    website_enabled: Boolean(values.website_enabled),
    website_type: values.website_type,
    website_runtime: values.website_runtime,
    website_key: values.website_key || null,
    website_deploy_url: values.website_deploy_url || null,
    custom_domain: values.custom_domain?.trim() || null,
    custom_domain_status: values.custom_domain_status || "none",
  });

  // Formik setup for edit form
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: "",
      email: "",
      phone: "",
      country_id: "",
      city_id: "",
      address: "",
      logo_url: "",
      gateway_fee_bearer: "member",
      convenience_fee_bearer: "member",
      convenience_fee_ngn: "",
      primary_color: DEFAULT_THEME.primary,
      secondary_color: DEFAULT_THEME.secondary,
      website_enabled: false,
      website_type: "NONE",
      website_runtime: "NONE",
      website_key: "",
      website_deploy_url: "",
      custom_domain: "",
      custom_domain_status: "none",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Cooperative name is required"),
      phone: Yup.string().required("Phone number is required"),
      country_id: Yup.string().required("Country is required"),
      city_id: Yup.string().required("City is required"),
      address: Yup.string().required("Address is required"),
      logo_url: Yup.string().url("Invalid logo URL"),
      gateway_fee_bearer: Yup.string().oneOf(["member", "organization"]).required("Gateway fee bearer is required"),
      convenience_fee_bearer: Yup.string().oneOf(["member", "organization"]).required("Convenience fee bearer is required"),
      convenience_fee_ngn: Yup.number().min(0, "Convenience fee cannot be negative").nullable(),
      primary_color: Yup.string().required("Primary color is required").matches(/^#[0-9A-Fa-f]{6}$/, "Enter a valid hex color"),
      secondary_color: Yup.string().required("Secondary color is required").matches(/^#[0-9A-Fa-f]{6}$/, "Enter a valid hex color"),
      website_type: Yup.string().oneOf(["NONE", "STANDARD", "CUSTOM"]),
      website_runtime: Yup.string().oneOf(["NONE", "STANDARD", "CUSTOM_SHARED", "DEDICATED_REPO"]),
      website_key: Yup.string().max(64).nullable(),
      website_deploy_url: Yup.string().url("Enter a valid deploy URL").nullable(),
    }),
    onSubmit: async (values) => {
      try {
        const payload = buildCooperativeUpdatePayload(values);

        const result = await updateCooperative({ id, ...payload }).unwrap();

        if (result.status) {
          toast.success("Cooperative updated successfully!");
          setIsEditMode(false);
          setIsPaymentFeeEditMode(false);
          refetchCooperative();

          // Dual-key Edge Config so vanity Host serves the public website (not only /login).
          const shouldPublishRouting =
            Boolean(values.custom_domain?.trim()) &&
            values.custom_domain_status === "active";
          if (shouldPublishRouting) {
            try {
              const publishResult = await publishWebsiteRouting(id).unwrap();
              if (publishResult?.status) {
                toast.success(
                  publishResult.message ||
                    "Website routing published (custom domain included).",
                );
              } else {
                toast.error(
                  publishResult?.message ||
                    "Saved, but failed to publish website routing. Click Publish website routing.",
                );
              }
            } catch (publishError) {
              toast.error(
                publishError?.data?.message ||
                  publishError?.message ||
                  "Saved, but failed to publish website routing. Click Publish website routing.",
              );
            }
          }
        } else {
          toast.error(result.message || "Failed to update cooperative");
        }
      } catch (error) {
        console.error("Error updating cooperative:", error);
        const errorMessage = error?.data?.message || error?.error?.message || error?.message || "Failed to update cooperative";
        toast.error(errorMessage);
      }
    },
  });
  
  // Initialize form values when cooperative data loads or edit mode changes
  useEffect(() => {
    if (cooperativeData?.data && !isEditMode) {
      const cooperative = cooperativeData.data;
      formik.setValues({
        name: cooperative.name || "",
        email: cooperative.email || "",
        phone: cooperative.phone || "",
        country_id: cooperative.country?.id?.toString() || "",
        city_id: cooperative.city?.id?.toString() || "",
        address: cooperative.address || "",
        logo_url: cooperative.logo_url || "",
        gateway_fee_bearer: cooperative.gateway_fee_bearer || "member",
        convenience_fee_bearer: cooperative.convenience_fee_bearer || "member",
        convenience_fee_ngn:
          cooperative.convenience_fee_ngn === null ||
          cooperative.convenience_fee_ngn === undefined
            ? ""
            : String(cooperative.convenience_fee_ngn),
        primary_color: normalizeTheme(cooperative).primary,
        secondary_color: normalizeTheme(cooperative).secondary,
        website_enabled: Boolean(cooperative.website_enabled),
        website_type: cooperative.website_type || "NONE",
        website_runtime: cooperative.website_runtime || "NONE",
        website_key: cooperative.website_key || "",
        website_deploy_url: cooperative.website_deploy_url || "",
        custom_domain: cooperative.custom_domain || "",
        custom_domain_status: cooperative.custom_domain_status || "none",
      });
      setLogoUrl(cooperative.logo_url || "");
      setLogoPreview("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cooperativeData, isEditMode]);

  // Filter cities by selected country
  const filteredCities = cities.filter(
    (city) => city.country_id?.toString() === formik.values.country_id
  );

  // Prepare options for selects
  const countryOptions = countries.map((country) => ({
    value: country.id.toString(),
    label: country.name,
  }));

  const cityOptions = filteredCities.map((city) => ({
    value: city.id.toString(),
    label: city.name,
  }));

  const handleLogoUpload = async (fileOrUrl) => {
    if (!fileOrUrl) return;

    // FileUploaderComp with acceptImages={false} uploads to Cloudinary automatically
    // and passes the URL string to onChange
    if (typeof fileOrUrl === "string") {
      setLogoUrl(fileOrUrl);
      formik.setFieldValue("logo_url", fileOrUrl);
      toast.success("Logo uploaded successfully!");
      return;
    }

    // If it's a File object (shouldn't happen with acceptImages={false}, but handle it just in case)
    if (fileOrUrl instanceof Blob || fileOrUrl instanceof File) {
      try {
        const previewUrl = URL.createObjectURL(fileOrUrl);
        setLogoPreview(previewUrl);
        const uploadedUrl = await uploadImage(fileOrUrl);
        setLogoUrl(uploadedUrl);
        formik.setFieldValue("logo_url", uploadedUrl);
        toast.success("Logo uploaded successfully!");
      } catch (error) {
        console.error("Error uploading logo:", error);
        toast.error("Failed to upload logo");
        if (logoPreview) {
          URL.revokeObjectURL(logoPreview);
          setLogoPreview("");
        }
      }
    }
  };

  const handleRemoveLogo = () => {
    if (logoPreview) {
      URL.revokeObjectURL(logoPreview);
    }
    setLogoUrl("");
    setLogoPreview("");
    formik.setFieldValue("logo_url", "");
  };

  const handleSavePaymentFees = async () => {
    const feeSchema = Yup.object({
      gateway_fee_bearer: Yup.string()
        .oneOf(["member", "organization"])
        .required(),
      convenience_fee_bearer: Yup.string()
        .oneOf(["member", "organization"])
        .required(),
      convenience_fee_ngn: Yup.number()
        .min(0, "Convenience fee cannot be negative")
        .nullable(),
    });
    try {
      await feeSchema.validate(
        {
          gateway_fee_bearer: formik.values.gateway_fee_bearer,
          convenience_fee_bearer: formik.values.convenience_fee_bearer,
          convenience_fee_ngn:
            formik.values.convenience_fee_ngn === "" ||
            formik.values.convenience_fee_ngn === null
              ? null
              : parseFloat(formik.values.convenience_fee_ngn),
        },
        { abortEarly: false },
      );
    } catch (err) {
      const msg = err?.inner?.[0]?.message || err?.message || "Invalid fee settings";
      toast.error(msg);
      return;
    }
    try {
      const payload = buildCooperativeUpdatePayload(formik.values);
      const result = await updateCooperative({ id, ...payload }).unwrap();
      if (result.status) {
        toast.success("Payment fee settings saved.");
        setIsPaymentFeeEditMode(false);
        refetchCooperative();
      } else {
        toast.error(result.message || "Failed to save payment fee settings");
      }
    } catch (error) {
      console.error("Error saving payment fees:", error);
      const errorMessage =
        error?.data?.message ||
        error?.error?.message ||
        error?.message ||
        "Failed to save payment fee settings";
      toast.error(errorMessage);
    }
  };

  const handleCancelPaymentFees = () => {
    setIsPaymentFeeEditMode(false);
    if (cooperativeData?.data) {
      const cooperative = cooperativeData.data;
      formik.setFieldValue(
        "gateway_fee_bearer",
        cooperative.gateway_fee_bearer || "member",
      );
      formik.setFieldValue(
        "convenience_fee_bearer",
        cooperative.convenience_fee_bearer || "member",
      );
      formik.setFieldValue(
        "convenience_fee_ngn",
        cooperative.convenience_fee_ngn === null ||
          cooperative.convenience_fee_ngn === undefined
          ? ""
          : String(cooperative.convenience_fee_ngn),
      );
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setIsPaymentFeeEditMode(false);
    if (cooperativeData?.data) {
      const cooperative = cooperativeData.data;
      formik.setValues({
        name: cooperative.name || "",
        email: cooperative.email || "",
        phone: cooperative.phone || "",
        country_id: cooperative.country?.id?.toString() || "",
        city_id: cooperative.city?.id?.toString() || "",
        address: cooperative.address || "",
        logo_url: cooperative.logo_url || "",
        gateway_fee_bearer: cooperative.gateway_fee_bearer || "member",
        convenience_fee_bearer: cooperative.convenience_fee_bearer || "member",
        convenience_fee_ngn:
          cooperative.convenience_fee_ngn === null ||
          cooperative.convenience_fee_ngn === undefined
            ? ""
            : String(cooperative.convenience_fee_ngn),
        primary_color: normalizeTheme(cooperative).primary,
        secondary_color: normalizeTheme(cooperative).secondary,
        website_enabled: Boolean(cooperative.website_enabled),
        website_type: cooperative.website_type || "NONE",
        website_runtime: cooperative.website_runtime || "NONE",
        website_key: cooperative.website_key || "",
        website_deploy_url: cooperative.website_deploy_url || "",
        custom_domain: cooperative.custom_domain || "",
        custom_domain_status: cooperative.custom_domain_status || "none",
      });
      setLogoUrl(cooperative.logo_url || "");
      setLogoPreview("");
    }
  };

  const handlePublishWebsiteRouting = async () => {
    try {
      const result = await publishWebsiteRouting(id).unwrap();
      if (result?.status) {
        toast.success(result.message || "Website routing published to Edge Config");
      } else {
        toast.error(result?.message || "Failed to publish website routing");
      }
    } catch (error) {
      toast.error(
        error?.data?.message || error?.message || "Failed to publish website routing",
      );
    }
  };

  const handleSendCredentials = () => {
    setConfirmAction('credentials');
    setShowConfirmModal(true);
  };

  const handleResendWelcome = () => {
    setConfirmAction('welcome');
    setShowConfirmModal(true);
  };

  const handleOpenSupportSession = async () => {
    if (!canStartSupportSession) {
      toast.error("Only platform Super Admins can open a support session.");
      return;
    }
    try {
      const result = await startSupportSession({
        companyId: id,
        branchId: selectedBranch?.value || undefined,
      }).unwrap();

      const token = result?.access_token;
      const isLocalManager =
        typeof window !== "undefined" &&
        /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname);
      // Local Manager always hands off to the local portal CRA (port 3000),
      // even if the API returns the coop's production domain_url.
      const portalUrl = (
        isLocalManager
          ? "http://localhost:3000"
          : result?.portal_url || ""
      ).replace(/\/$/, "");
      const supportMeta = result?.support_session || {};

      if (!token || !portalUrl || portalUrl === "N/A") {
        toast.error(
          result?.message ||
            "Support session created but portal URL is unavailable.",
        );
        return;
      }

      const params = new URLSearchParams({
        token,
        expires_at: supportMeta.expires_at || result.expires_at || "",
        admin_name: supportMeta.admin_name || managerUser?.name || "",
        company_name: supportMeta.company_name || "",
      });

      window.open(
        `${portalUrl}/support/session?${params.toString()}`,
        "_blank",
        "noopener,noreferrer",
      );
      toast.success("Support session opened. It expires in 30 minutes.");
    } catch (error) {
      toast.error(
        error?.data?.message ||
          error?.message ||
          "Failed to start support session",
      );
    }
  };

  const handleConfirmAction = async () => {
    setIsSendingAction(true);
    try {
      if (confirmAction === 'credentials') {
        await sendCredentials(id).unwrap();
        toast.success('Login credentials email sent successfully!');
      } else if (confirmAction === 'welcome') {
        await resendWelcome(id).unwrap();
        toast.success('Welcome email sent successfully!');
      }
      setShowConfirmModal(false);
      setConfirmAction(null);
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error(error?.data?.message || `Failed to send ${confirmAction === 'credentials' ? 'credentials' : 'welcome'} email`);
    } finally {
      setIsSendingAction(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
    setConfirmAction(null);
  };

  const renderOverview = () => {
    const cooperative = cooperativeData?.data;
    if (isLoadingCooperative && !cooperative) {
      return (
        <div className="space-y-4 animate-pulse" aria-busy="true">
          <div className="h-10 bg-gray-200 rounded w-1/3" />
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-24 bg-gray-100 rounded-xl" />
            ))}
          </div>
          <div className="h-40 bg-gray-100 rounded-xl" />
        </div>
      );
    }

    const subscription = cooperative?.subscription;
    const subscriptionPlanName = subscription?.plan?.name || "N/A";
    const portalExpiresAt = subscription?.expires_at || null;
    const getDaysLeft = (expiry) => {
      if (!expiry) return null;
      const end = new Date(expiry);
      if (Number.isNaN(end.getTime())) return null;
      const diff = end.getTime() - Date.now();
      return Math.ceil(diff / (1000 * 60 * 60 * 24));
    };
    const daysLeft = getDaysLeft(portalExpiresAt);
    const isExpired = daysLeft !== null && daysLeft <= 0;
    const isExpiringSoon = daysLeft !== null && daysLeft > 0 && daysLeft <= 30;
    const expiryLabel = portalExpiresAt
      ? formatListDate(portalExpiresAt)
      : "Not set";
    const daysHint =
      daysLeft === null
        ? "No subscription expiry on file"
        : isExpired
          ? "Portal access expired — renew subscription"
          : daysLeft === 1
            ? "Expires tomorrow"
            : `${daysLeft} day${daysLeft === 1 ? "" : "s"} remaining`;

    const scopeLabel =
      cooperative?.scope === "branch" || selectedBranch?.value
        ? selectedBranch?.label || "Selected branch"
        : "All branches";

    const totalCredit = Number(cooperative?.total_credit ?? 0);
    const totalDebit = Number(cooperative?.total_debit ?? 0);
    const netBalance = totalCredit - totalDebit;
    const totalFlow = totalCredit + totalDebit;
    const creditRatio = totalFlow > 0 ? (totalCredit / totalFlow) * 100 : 0;

    const smsNaira =
      cooperative?.smsBalanceNaira != null
        ? Number(cooperative.smsBalanceNaira)
        : Number(cooperative?.smsBalanceKobo ?? cooperative?.smsBalance ?? 0) / 100;

    const members =
      cooperative?.onboardedMembers ?? cooperative?.total_members ?? 0;
    const staff = cooperative?.onboardedStaff ?? 0;

    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 min-w-0">
            <h2 className="text-xl sm:text-2xl font-bold text-[#383838] truncate">
              Overview
            </h2>
            {subscriptionPlanName !== "N/A" && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-[#8b313a]/10 text-[#8b313a] w-fit">
                {subscriptionPlanName}
              </span>
            )}
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold w-fit ${
                isExpired
                  ? "bg-red-100 text-red-700"
                  : isExpiringSoon
                    ? "bg-amber-100 text-amber-800"
                    : "bg-[#eaf1f4] text-[#31778c]"
              }`}
              title="Portal subscription expiry"
            >
              <FaCalendarAlt aria-hidden />
              Portal expires: {expiryLabel}
            </span>
            <span className="text-xs text-gray-500">{scopeLabel}</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <span className="text-sm text-gray-600 whitespace-nowrap">
              Disable cooperative
            </span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#8b313a]"></div>
            </label>
          </div>
        </div>

        {(isExpired || isExpiringSoon || !portalExpiresAt) && (
          <div
            className={`border p-4 rounded-xl flex items-start gap-3 ${
              isExpired
                ? "bg-red-50 border-red-200"
                : !portalExpiresAt
                  ? "bg-amber-50 border-amber-200"
                  : "bg-amber-50 border-amber-200"
            }`}
            role="status"
          >
            <div
              className={`p-2 rounded-full shrink-0 ${
                isExpired ? "bg-red-100" : "bg-amber-100"
              }`}
            >
              <FaCalendarAlt
                className={isExpired ? "text-red-600" : "text-amber-700"}
              />
            </div>
            <div className="min-w-0">
              <p
                className={`font-semibold ${
                  isExpired ? "text-red-800" : "text-amber-900"
                }`}
              >
                {isExpired
                  ? "Portal subscription expired"
                  : !portalExpiresAt
                    ? "Portal expiry unavailable"
                    : "Portal subscription expiring soon"}
              </p>
              <p
                className={`text-sm mt-0.5 ${
                  isExpired ? "text-red-700" : "text-amber-800"
                }`}
              >
                {!portalExpiresAt
                  ? "No expires_at on this cooperative’s subscription. Staff may see “Subscription status is unavailable” in the portal until a plan end date is set."
                  : isExpired
                    ? `${cooperative?.name || "This cooperative"}’s portal expired on ${expiryLabel}. Renew the subscription to restore access.`
                    : `${cooperative?.name || "This cooperative"}’s portal expires on ${expiryLabel} (${daysLeft} day${daysLeft === 1 ? "" : "s"} left). Notify them to renew.`}
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <MetricCard
            label="Portal expires"
            value={expiryLabel}
            hint={daysHint}
          />
          <MetricCard
            label="Members"
            value={Number(members).toLocaleString()}
            hint="Active onboarded members"
          />
          <MetricCard
            label="Staff"
            value={Number(staff).toLocaleString()}
            hint="Staff in scope"
          />
          <MetricCard
            label="Loan disbursed"
            value={formatMoney(Number(cooperative?.loanDisbursed ?? 0).toFixed(2), true)}
            hint={`${Number(cooperative?.loans_count ?? loansTotal).toLocaleString()} disbursed loan(s)`}
          />
          <MetricCard
            label="Unpaid loans"
            value={formatMoney(Number(cooperative?.unpaidLoans ?? 0).toFixed(2), true)}
            hint="Pending repayments"
          />
          <MetricCard
            label="Total contributed"
            value={formatMoney(
              Number(
                cooperative?.total_contribution ??
                  cooperative?.total_contributed ??
                  0
              ).toFixed(2),
              true
            )}
            hint={`${Number(cooperative?.contributions_count ?? contributionsTotal).toLocaleString()} contribution record(s)`}
          />
          <MetricCard
            label="Available savings"
            value={formatMoney(Number(cooperative?.availableSavings ?? 0).toFixed(2), true)}
            hint="After active withdrawal requests"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl p-4 md:p-6 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-4">
              <h3 className="text-lg font-semibold text-[#383838]">
                Ledger summary
              </h3>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#19bc31]" /> Credit
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#ff4343]" /> Debit
                </span>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <div className="rounded-lg bg-green-50 border border-green-100 p-3 text-center">
                <p className="text-xs text-gray-500 mb-1">Total credit</p>
                <p className="text-lg font-bold text-[#19bc31] break-words">
                  {formatMoney(totalCredit.toFixed(2), true)}
                </p>
              </div>
              <div className="rounded-lg bg-red-50 border border-red-100 p-3 text-center">
                <p className="text-xs text-gray-500 mb-1">Total debit</p>
                <p className="text-lg font-bold text-[#ff4343] break-words">
                  {formatMoney(totalDebit.toFixed(2), true)}
                </p>
              </div>
              <div className="rounded-lg bg-[#eaf1f4] border border-[#31778c]/20 p-3 text-center">
                <p className="text-xs text-gray-500 mb-1">Net</p>
                <p
                  className={`text-lg font-bold break-words ${
                    netBalance >= 0 ? "text-[#31778c]" : "text-[#ff4343]"
                  }`}
                >
                  {formatMoney(netBalance.toFixed(2), true)}
                </p>
              </div>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
              <div
                className="bg-[#19bc31] h-2 rounded-full"
                style={{ width: `${Math.min(100, Math.max(0, creditRatio))}%` }}
              />
            </div>
            <p className="text-center text-sm text-gray-500 mt-2">
              Credit share of total flow: {creditRatio.toFixed(1)}%
              {totalFlow <= 0 ? " (no credit/debit yet)" : ""}
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4 md:p-6 shadow-sm flex flex-col">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              SMS wallet
            </p>
            <p className="text-3xl font-bold text-[#8b313a] mb-1">
              {formatMoney(smsNaira.toFixed(2), true)}
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Remaining balance · {scopeLabel}
            </p>
            <p className="text-xs text-gray-400 mb-4 flex-1">
              Cooperatives top up from their portal. Review paid top-ups in SMS
              Messaging.
            </p>
            <Link
              to={`/sms-messaging/top_up_orders?company_id=${id}`}
              className="inline-flex justify-center items-center w-full bg-[#8b313a] hover:opacity-90 text-white py-2.5 px-4 rounded-lg text-sm font-medium transition"
            >
              View SMS top-ups
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          <CardComp cardClassName="p-4 md:p-6 border border-gray-200 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-4">
              <h3 className="text-lg font-semibold text-[#383838]">
                Recent loans
              </h3>
              <span className="text-sm text-gray-500">
                Total: {Number(cooperative?.loans_count ?? loansTotal).toLocaleString()}
              </span>
            </div>
            {loansLoading ? (
              <div className="flex justify-center py-8">
                <Loader size="sm" />
              </div>
            ) : loans.length > 0 ? (
              <div className="space-y-3">
                {loans.slice(0, 5).map((loan) => (
                  <div
                    key={loan.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-800 text-sm truncate">
                        {loan.member?.first_name} {loan.member?.last_name}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {loan.loan_type_name || loan.status} ·{" "}
                        {loan.created_at
                          ? new Date(loan.created_at).toLocaleDateString()
                          : "—"}
                      </p>
                    </div>
                    <p className="font-semibold text-sm text-gray-800 ml-2">
                      {formatMoney(Number(loan.amount || 0).toFixed(2), true)}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center py-8 text-gray-500 text-sm">
                No loans found
              </p>
            )}
          </CardComp>

          <CardComp cardClassName="p-4 md:p-6 border border-gray-200 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-4">
              <h3 className="text-lg font-semibold text-[#383838]">
                Recent transactions
              </h3>
              <span className="text-sm text-gray-500">
                Total:{" "}
                {Number(
                  cooperative?.transactions_count ?? transactionsTotal
                ).toLocaleString()}
              </span>
            </div>
            {transactionsLoading ? (
              <div className="flex justify-center py-8">
                <Loader size="sm" />
              </div>
            ) : transactions.length > 0 ? (
              <div className="space-y-3">
                {transactions.slice(0, 5).map((transaction) => (
                  <div
                    key={transaction.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-800 text-sm truncate">
                        {transaction.type}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {transaction.created_at
                          ? new Date(transaction.created_at).toLocaleString()
                          : "—"}
                      </p>
                    </div>
                    <div className="text-right ml-2">
                      <p className="font-semibold text-sm text-gray-800">
                        {formatMoney(
                          Number(transaction.amount || 0).toFixed(2),
                          true
                        )}
                      </p>
                      <span className="text-xs text-gray-500">
                        {transaction.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center py-8 text-gray-500 text-sm">
                No transactions found
              </p>
            )}
          </CardComp>
        </div>
      </div>
    );
  };

  const renderLoans = () => (
    <div className="space-y-6">
      <CardComp cardClassName="p-0 overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 px-5 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-[#383838]">Loan Management</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {loansTotal} loan{loansTotal === 1 ? "" : "s"}
              {selectedBranch?.value ? ` · ${selectedBranch.label}` : " · all branches"}
            </p>
          </div>
        </div>

        {loansLoading ? (
          <div className="p-8 flex justify-center">
            <Loader size="md" />
          </div>
        ) : loans.length > 0 ? (
          <div className="px-4 pb-4">
            <div className="overflow-x-auto rounded-[10px] border border-[#e0e0e0]">
              <table className="w-full min-w-[720px] border-collapse">
                <thead>
                  <tr className="bg-[#383838]">
                    <th className={tableHeadClass}>Member</th>
                    <th className={tableHeadClass}>Loan Type</th>
                    <th className={tableHeadClass}>Amount</th>
                    <th className={tableHeadClass}>Duration</th>
                    <th className={tableHeadClass}>Status</th>
                    <th className={tableHeadClass}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {loans.map((loan) => (
                    <tr key={loan.id} className="bg-white hover:bg-[#fafafa]">
                      <td className={tableCellClass}>
                        <div className="flex items-center min-w-[160px]">
                          <img
                            src={loan.member?.photo || "/Images/Dashboard/image1.svg"}
                            alt=""
                            className="w-8 h-8 rounded-full mr-3 object-cover shrink-0"
                          />
                          <div>
                            <p className="font-medium text-[#383838]">
                              {loan.member?.first_name} {loan.member?.last_name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {loan.member?.member_no
                                ? `Membership No: ${loan.member.member_no}`
                                : `ID: ${loan.member_id}`}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className={tableCellClass}>
                        <span className="inline-block px-2.5 py-1 bg-[#e1ecfe] text-[#0556e5] rounded-full text-xs font-medium">
                          {loan.loan_type_name || "—"}
                        </span>
                      </td>
                      <td className={`${tableCellClass} font-semibold whitespace-nowrap`}>
                        {formatMoney(
                          Number(loan.amount ?? 0).toFixed(2),
                          true
                        )}
                      </td>
                      <td className={tableCellClass}>
                        {loan.duration} {loan.duration_type}
                      </td>
                      <td className={tableCellClass}>
                        <span
                          className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium text-white ${
                            loan.status === "PENDING"
                              ? "bg-[#FFA500]"
                              : loan.status === "APPROVED" || loan.status === "DISBURSED"
                              ? "bg-[#19BC31]"
                              : "bg-[#FF4343]"
                          }`}
                        >
                          {loan.status || "—"}
                        </span>
                      </td>
                      <td className={`${tableCellClass} whitespace-nowrap`}>
                        {formatListDate(loan.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ListPagination
              page={loansPage}
              lastPage={loansLastPage}
              total={loansTotal}
              perPage={listPerPage}
              onPageChange={setLoansPage}
            />
          </div>
        ) : (
          <div className="text-center py-12 px-4">
            <p className="text-gray-500">No loans found</p>
          </div>
        )}
      </CardComp>
    </div>
  );

  const renderContributions = () => (
    <div className="space-y-6">
      <CardComp cardClassName="p-0 overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 px-5 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-[#383838]">
              Member Contributions
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {contributionsTotal} record{contributionsTotal === 1 ? "" : "s"}
              {selectedBranch?.value ? ` · ${selectedBranch.label}` : " · all branches"}
            </p>
          </div>
        </div>

        {contributionsLoading ? (
          <div className="p-8 flex justify-center">
            <Loader size="md" />
          </div>
        ) : contributions.length > 0 ? (
          <div className="px-4 pb-4">
            <div className="overflow-x-auto rounded-[10px] border border-[#e0e0e0]">
              <table className="w-full min-w-[640px] border-collapse">
                <thead>
                  <tr className="bg-[#383838]">
                    <th className={tableHeadClass}>Member</th>
                    <th className={tableHeadClass}>Amount</th>
                    <th className={tableHeadClass}>Type</th>
                    <th className={tableHeadClass}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {contributions.map((contribution) => {
                    const amount = Number(
                      contribution.amount ?? contribution.total_amount ?? 0
                    );
                    const typeLabel =
                      contribution.type ||
                      contribution.contributionCategory ||
                      contribution.category?.name ||
                      contribution.contributionType?.name ||
                      "—";
                    const memberNo =
                      contribution.member?.member_no ||
                      contribution.member?.member_number;

                    return (
                      <tr
                        key={contribution.id}
                        className="bg-white hover:bg-[#fafafa]"
                      >
                        <td className={tableCellClass}>
                          <div className="flex items-center min-w-[180px]">
                            <img
                              src={
                                contribution.member?.photo ||
                                "/Images/Dashboard/image1.svg"
                              }
                              alt=""
                              className="w-8 h-8 rounded-full mr-3 object-cover shrink-0"
                            />
                            <div>
                              <p className="font-medium text-[#383838]">
                                {contribution.member?.first_name}{" "}
                                {contribution.member?.last_name}
                              </p>
                              {memberNo ? (
                                <p className="text-xs text-gray-500">
                                  Membership No: {memberNo}
                                </p>
                              ) : null}
                            </div>
                          </div>
                        </td>
                        <td className={`${tableCellClass} font-semibold whitespace-nowrap`}>
                          {formatMoney(amount.toFixed(2), true)}
                        </td>
                        <td className={tableCellClass}>
                          <span className="inline-block px-2.5 py-1 bg-[#ddfcdc] text-[#19BC31] rounded-full text-xs font-medium">
                            {typeLabel}
                          </span>
                        </td>
                        <td className={`${tableCellClass} whitespace-nowrap`}>
                          {formatListDate(contribution.created_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <ListPagination
              page={contributionsPage}
              lastPage={contributionsLastPage}
              total={contributionsTotal}
              perPage={listPerPage}
              onPageChange={setContributionsPage}
            />
          </div>
        ) : (
          <div className="text-center py-12 px-4">
            <p className="text-gray-500">No contributions found</p>
          </div>
        )}
      </CardComp>
    </div>
  );

  const renderTransactions = () => (
    <div className="space-y-6">
      <CardComp cardClassName="p-0 overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 px-5 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-[#383838]">
              Transaction History
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {transactionsTotal} transaction{transactionsTotal === 1 ? "" : "s"}
              {selectedBranch?.value ? ` · ${selectedBranch.label}` : " · all branches"}
            </p>
          </div>
        </div>

        {transactionsLoading ? (
          <div className="p-8 flex justify-center">
            <Loader size="md" />
          </div>
        ) : transactions.length > 0 ? (
          <div className="px-4 pb-4">
            <div className="overflow-x-auto rounded-[10px] border border-[#e0e0e0]">
              <table className="w-full min-w-[800px] border-collapse">
                <thead>
                  <tr className="bg-[#383838]">
                    <th className={tableHeadClass}>Reference</th>
                    <th className={tableHeadClass}>Member</th>
                    <th className={tableHeadClass}>Type</th>
                    <th className={tableHeadClass}>Amount</th>
                    <th className={tableHeadClass}>Status</th>
                    <th className={tableHeadClass}>Date</th>
                    <th className={tableHeadClass}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction) => {
                    const typeLabel =
                      transaction.display_type || transaction.type || "—";
                    const amount = Number(
                      transaction.signed_amount ?? transaction.amount ?? 0
                    );
                    return (
                      <tr
                        key={transaction.id}
                        className="bg-white hover:bg-[#fafafa]"
                      >
                        <td className={`${tableCellClass} font-mono text-xs text-gray-600 max-w-[140px] truncate`} title={transaction.reference || ""}>
                          {transaction.reference || "—"}
                        </td>
                        <td className={tableCellClass}>
                          <div className="flex items-center min-w-[140px]">
                            <img
                              src={
                                transaction.member?.photo ||
                                "/Images/Dashboard/image1.svg"
                              }
                              alt=""
                              className="w-8 h-8 rounded-full mr-3 object-cover shrink-0"
                            />
                            <p className="font-medium text-[#383838]">
                              {transaction.member?.first_name}{" "}
                              {transaction.member?.last_name}
                            </p>
                          </div>
                        </td>
                        <td className={tableCellClass}>
                          <span className="inline-block px-2.5 py-1 bg-[#e1ecfe] text-[#0556e5] rounded-full text-xs font-medium max-w-[180px] truncate" title={typeLabel}>
                            {typeLabel}
                          </span>
                        </td>
                        <td
                          className={`${tableCellClass} font-semibold whitespace-nowrap`}
                          style={{
                            color:
                              amount < 0
                                ? "#d40000"
                                : amount > 0
                                ? "#19BC31"
                                : "#383838",
                          }}
                        >
                          {formatMoney(Math.abs(amount).toFixed(2), true)}
                          {amount < 0 ? " out" : amount > 0 ? " in" : ""}
                        </td>
                        <td className={tableCellClass}>
                          <span
                            className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium text-white ${
                              ["SUCCESSFUL", "SUCCESS", "PROCESSED", "PAID"].includes(
                                String(transaction.status || "").toUpperCase()
                              )
                                ? "bg-[#19BC31]"
                                : String(transaction.status || "").toUpperCase() ===
                                  "PENDING"
                                ? "bg-[#FFA500]"
                                : "bg-[#FF4343]"
                            }`}
                          >
                            {transaction.status || "—"}
                          </span>
                        </td>
                        <td className={`${tableCellClass} whitespace-nowrap`}>
                          {formatListDate(transaction.created_at)}
                        </td>
                        <td className={tableCellClass}>
                          <button
                            type="button"
                            onClick={() => setSelectedTransaction(transaction)}
                            className="px-3 py-1.5 text-sm font-medium rounded-md border-2 border-[#8b313a] text-[#8b313a] hover:bg-[rgba(139,49,58,0.06)] whitespace-nowrap"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <ListPagination
              page={transactionsPage}
              lastPage={transactionsLastPage}
              total={transactionsTotal}
              perPage={listPerPage}
              onPageChange={setTransactionsPage}
            />
          </div>
        ) : (
          <div className="text-center py-12 px-4">
            <p className="text-gray-500">No transactions found</p>
          </div>
        )}
      </CardComp>
    </div>
  );

  const renderStatistics = () => (
    <div className="space-y-6">
      <CardComp cardClassName="p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-800">
            Financial Statistics
          </h3>
          <select
            value={statisticsFilter}
            onChange={(e) => setStatisticsFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8B313A]"
          >
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>

        {statisticsLoading ? (
          <Loader size="md" />
        ) : statistics.length > 0 ? (
          <div className="space-y-4">
            {statistics.map((stat, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="font-medium text-gray-800">{stat.week}</h4>
                  <div className="flex space-x-4">
                    <div className="text-center">
                      <p className="text-sm text-gray-500">Credit</p>
                      <p className="text-lg font-semibold text-green-600">
                        {formatMoney(stat.credit)}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-500">Debit</p>
                      <p className="text-lg font-semibold text-red-600">
                        {formatMoney(stat.debit)}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-green-500 to-red-500 h-2 rounded-full"
                    style={{
                      width: `${
                        stat.credit > 0
                          ? (stat.credit / (stat.credit + stat.debit)) * 100
                          : 0
                      }%`,
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-gray-400"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z" />
              </svg>
            </div>
            <p className="text-gray-500">No statistics available</p>
          </div>
        )}
      </CardComp>
    </div>
  );

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const calculateExpiryDate = (startDate, duration, durationType) => {
    if (!startDate || !duration || !durationType) return "N/A";
    const start = new Date(startDate);
    let expiry = new Date(start);
    
    switch (durationType) {
      case "DAYS":
        expiry.setDate(start.getDate() + parseInt(duration));
        break;
      case "WEEKS":
        expiry.setDate(start.getDate() + parseInt(duration) * 7);
        break;
      case "MONTHS":
        expiry.setMonth(start.getMonth() + parseInt(duration));
        break;
      case "YEARS":
        expiry.setFullYear(start.getFullYear() + parseInt(duration));
        break;
      default:
        return "N/A";
    }
    
    return formatDate(expiry);
  };

  const renderDetails = () => {
    // Handle loading state
    if (isLoadingCooperative) {
      return (
        <CardComp>
          <div className="text-center py-8">
            <Loader />
          </div>
        </CardComp>
      );
    }

    // Handle error state
    if (isErrorCooperative) {
      return (
        <CardComp>
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Error Loading Cooperative</h3>
            <p className="text-gray-600 mb-4">
              {cooperativeError?.data?.message || cooperativeError?.message || "Failed to load cooperative details"}
            </p>
            <button
              onClick={() => refetchCooperative()}
              className="px-4 py-2 bg-[#8B313A] text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </CardComp>
      );
    }

    const cooperative = cooperativeData?.data;
    if (!cooperative) {
      return (
        <CardComp>
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaHome className="text-gray-400 text-3xl" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">No Cooperative Data</h3>
            <p className="text-gray-600">No cooperative information found.</p>
          </div>
        </CardComp>
      );
    }

    const subscription = cooperative?.subscription;
    const mainDomain = "uniquecoop.net";
    const subdomainUrl = cooperative?.domain_url
      ? `https://${cooperative.domain_url}.${mainDomain}`
      : "N/A";
    const cooperativeUrl =
      cooperative?.public_url ||
      (cooperative?.custom_domain_status === "active" && cooperative?.custom_domain
        ? `https://${cooperative.custom_domain}`
        : subdomainUrl);

    const getDaysLeft = (expiry) => {
      if (!expiry) return 0;
      const now = new Date();
      const end = new Date(expiry);
      const diff = end - now;
      return diff > 0 ? Math.ceil(diff / (1000 * 60 * 60 * 24)) : 0;
    };

    const daysLeft = subscription?.expires_at ? getDaysLeft(subscription.expires_at) : 0;

    return (
      <CardComp>
        <div className="p-4 md:p-6">
          {/* Email Actions Alert */}
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <div className="bg-blue-100 p-3 rounded-full mr-4">
                  <FaEnvelope className="text-blue-500 text-2xl" />
                </div>
                <div>
                  <p className="text-blue-800 font-semibold text-lg">
                    Email Management
                  </p>
                  <p className="text-blue-700 text-sm">
                    Send emails to {cooperative?.email || 'the cooperative administrator'}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button 
                onClick={handleResendWelcome}
                disabled={isSendingAction}
                className="bg-green-700 text-white px-4 py-2 rounded-md text-sm hover:bg-green-800 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <FaEnvelope className="text-sm" />
                <span>Resend Welcome Email</span>
              </button>
              <button 
                onClick={handleSendCredentials}
                disabled={isSendingAction}
                className="bg-blue-700 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-800 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <FaEnvelope className="text-sm" />
                <span>Send Login Credentials</span>
              </button>
              {canStartSupportSession && (
                <button
                  onClick={handleOpenSupportSession}
                  disabled={isStartingSupport}
                  className="bg-[#8B313A] text-white px-4 py-2 rounded-md text-sm hover:bg-[#6B252A] transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  title="Open this cooperative portal as Platform Support (30 minutes)"
                >
                  <FaUserShield className="text-sm" />
                  <span>
                    {isStartingSupport
                      ? "Opening…"
                      : "Open Support Session"}
                  </span>
                </button>
              )}
            </div>
          </div>

          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-gray-800">Cooperative Information</h3>
            {!isEditMode ? (
              <button
                type="button"
                onClick={() => {
                  setIsPaymentFeeEditMode(false);
                  setIsEditMode(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-[#8B313A] text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <FaEdit className="text-sm" />
                <span>Edit</span>
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCancelEdit}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  <FaTimes className="text-sm" />
                  <span>Cancel</span>
                </button>
                <button
                  onClick={formik.handleSubmit}
                  disabled={isUpdating}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaCheck className="text-sm" />
                  <span>{isUpdating ? "Saving..." : "Save"}</span>
                </button>
              </div>
            )}
          </div>
          
          <form onSubmit={formik.handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Logo Section */}
              <div className="md:col-span-2 flex items-center space-x-4 mb-6 pb-6 border-b">
                {isEditMode ? (
                  <div className="flex items-center space-x-4">
                    {(logoPreview || logoUrl || cooperative?.logo_url) ? (
                      <div className="relative">
                        <img
                          src={logoPreview || logoUrl || cooperative?.logo_url}
                          alt="Logo Preview"
                          className="w-24 h-24 rounded-lg object-cover border-2 border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={handleRemoveLogo}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <div className="w-24 h-24 rounded-lg bg-gray-200 flex items-center justify-center">
                        <FaHome className="text-gray-400 text-3xl" />
                      </div>
                    )}
                    <div>
                      <FileUploaderComp
                        LabelText="Upload Logo"
                        bodyText="Click to upload or drag and drop your logo here"
                        onChange={handleLogoUpload}
                        acceptImages={false}
                        required={false}
                      />
                      {formik.errors.logo_url && (
                        <p className="text-red-500 text-xs mt-1">{formik.errors.logo_url}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    {cooperative?.logo_url ? (
                      <img
                        src={cooperative.logo_url}
                        alt={cooperative.name || "Cooperative Logo"}
                        className="w-24 h-24 rounded-lg object-cover border-2 border-gray-200"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-lg bg-gray-200 flex items-center justify-center">
                        <FaHome className="text-gray-400 text-3xl" />
                      </div>
                    )}
                    <div>
                      <h4 className="text-lg font-semibold text-gray-800">{cooperative?.name || "N/A"}</h4>
                      <p className="text-sm text-gray-500">Cooperative Logo</p>
                    </div>
                  </>
                )}
              </div>

            {/* Basic Information */}
            <div className="space-y-4">
              <h4 className="text-md font-semibold text-gray-700 mb-4 flex items-center">
                <FaInfoCircle className="mr-2 text-[#8B313A]" />
                Basic Information
              </h4>
              
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-500">Cooperative Name</label>
                  {isEditMode ? (
                    <>
                      <Input
                        name="name"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="mt-1"
                        placeholder="Enter cooperative name"
                      />
                      {formik.touched.name && formik.errors.name && (
                        <p className="text-red-500 text-xs mt-1">{formik.errors.name}</p>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-800 mt-1">{cooperative?.name || "N/A"}</p>
                  )}
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500 flex items-center">
                    <FaEnvelope className="mr-2 text-gray-400" />
                    Email Address <span className="text-gray-400 text-xs">(Read-only)</span>
                  </label>
                  <p className="text-gray-800 mt-1 bg-gray-100 px-2 py-1 rounded">{cooperative?.email || "N/A"}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500 flex items-center">
                    <FaPhone className="mr-2 text-gray-400" />
                    Phone Number
                  </label>
                  {isEditMode ? (
                    <>
                      <Input
                        name="phone"
                        value={formik.values.phone}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="mt-1"
                        placeholder="Enter phone number"
                      />
                      {formik.touched.phone && formik.errors.phone && (
                        <p className="text-red-500 text-xs mt-1">{formik.errors.phone}</p>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-800 mt-1">{cooperative?.phone || "N/A"}</p>
                  )}
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500 flex items-center">
                    <FaMapMarkerAlt className="mr-2 text-gray-400" />
                    Address
                  </label>
                  {isEditMode ? (
                    <>
                      <Input
                        name="address"
                        value={formik.values.address}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="mt-1"
                        placeholder="Enter address"
                      />
                      {formik.touched.address && formik.errors.address && (
                        <p className="text-red-500 text-xs mt-1">{formik.errors.address}</p>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-800 mt-1">{cooperative?.address || "N/A"}</p>
                  )}
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Portal Branding</label>
                  {isEditMode ? (
                    <div className="mt-2">
                      <ThemeBrandingEditor
                        primaryColor={formik.values.primary_color}
                        secondaryColor={formik.values.secondary_color}
                        onPrimaryChange={(color) => formik.setFieldValue("primary_color", color)}
                        onSecondaryChange={(color) => formik.setFieldValue("secondary_color", color)}
                        onResetDefaults={() => {
                          formik.setFieldValue("primary_color", DEFAULT_THEME.primary);
                          formik.setFieldValue("secondary_color", DEFAULT_THEME.secondary);
                        }}
                        logoUrl={logoPreview || logoUrl || cooperative?.logo_url}
                        companyName={formik.values.name || cooperative?.name || "Cooperative Name"}
                      />
                    </div>
                  ) : (
                    <div className="mt-2 flex flex-wrap gap-4">
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block w-8 h-8 rounded border border-gray-200"
                          style={{ backgroundColor: normalizeTheme(cooperative).primary }}
                        />
                        <span className="text-sm text-gray-700">
                          Primary: {normalizeTheme(cooperative).primary}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block w-8 h-8 rounded border border-gray-200"
                          style={{ backgroundColor: normalizeTheme(cooperative).secondary }}
                        />
                        <span className="text-sm text-gray-700">
                          Secondary: {normalizeTheme(cooperative).secondary}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Location & Domain */}
            <div className="space-y-4">
              <h4 className="text-md font-semibold text-gray-700 mb-4 flex items-center">
                <FaGlobe className="mr-2 text-[#8B313A]" />
                Location & Domain
              </h4>
              
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-500">Country</label>
                  {isEditMode ? (
                    <>
                      <CustomSelect
                        name="country_id"
                        label=""
                        value={countryOptions.find(
                          (option) => option.value === formik.values.country_id
                        )}
                        onChange={(selectedOption) => {
                          formik.setFieldValue("country_id", selectedOption?.value || "");
                          formik.setFieldValue("city_id", ""); // Reset city when country changes
                        }}
                        options={countryOptions}
                        placeholder="Select country"
                        styles={customStyles}
                        isLoading={isLoadingCountries}
                      />
                      {formik.touched.country_id && formik.errors.country_id && (
                        <p className="text-red-500 text-xs mt-1">{formik.errors.country_id}</p>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-800 mt-1">{cooperative?.country?.name || "N/A"}</p>
                  )}
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">City</label>
                  {isEditMode ? (
                    <>
                      <CustomSelect
                        name="city_id"
                        label=""
                        value={cityOptions.find(
                          (option) => option.value === formik.values.city_id
                        )}
                        onChange={(selectedOption) => {
                          formik.setFieldValue("city_id", selectedOption?.value || "");
                        }}
                        options={cityOptions}
                        placeholder={formik.values.country_id ? "Select city" : "Select country first"}
                        styles={customStyles}
                        isLoading={isLoadingCities}
                        isDisabled={!formik.values.country_id}
                      />
                      {formik.touched.city_id && formik.errors.city_id && (
                        <p className="text-red-500 text-xs mt-1">{formik.errors.city_id}</p>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-800 mt-1">{cooperative?.city?.name || "N/A"}</p>
                  )}
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Domain slug <span className="text-gray-400 text-xs">(Read-only)</span></label>
                  <p className="text-gray-800 mt-1 font-mono bg-gray-100 px-2 py-1 rounded">{cooperative?.domain_url || "N/A"}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Platform subdomain URL</label>
                  <p className="text-gray-800 mt-1 font-mono text-sm break-all bg-gray-100 px-2 py-1 rounded">
                    {subdomainUrl}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Primary public URL</label>
                  <p className="text-gray-800 mt-1 font-mono text-sm break-all bg-gray-100 px-2 py-1 rounded">
                    {cooperativeUrl}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Custom domain</label>
                  {isEditMode ? (
                    <Input
                      name="custom_domain"
                      value={formik.values.custom_domain}
                      onChange={formik.handleChange}
                      placeholder="www.theircoop.com"
                    />
                  ) : (
                    <p className="text-gray-800 mt-1 font-mono text-sm break-all bg-gray-100 px-2 py-1 rounded">
                      {cooperative?.custom_domain || "—"}
                    </p>
                  )}
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Custom domain status</label>
                  {isEditMode ? (
                    <select
                      name="custom_domain_status"
                      value={formik.values.custom_domain_status}
                      onChange={formik.handleChange}
                      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    >
                      <option value="none">none (cleared)</option>
                      <option value="pending">pending (DNS/SSL in progress)</option>
                      <option value="active">active (live for Host resolve)</option>
                      <option value="failed">failed</option>
                    </select>
                  ) : (
                    <p className="text-gray-800 mt-1">
                      {cooperative?.custom_domain_status || "none"}
                    </p>
                  )}
                  {isEditMode && (
                    <p className="text-xs text-gray-500 mt-2">
                      Customer CNAME www → Vercel gateway, add the hostname on the gateway project for SSL,
                      set status to <span className="font-mono">active</span>, then Publish website routing.
                      Platform subdomain keeps working alongside the custom domain.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Public website configuration */}
            <div className="md:col-span-2 space-y-4 mt-6 pt-6 border-t">
              <h4 className="text-md font-semibold text-gray-700 mb-4 flex items-center">
                <FaGlobe className="mr-2 text-[#8B313A]" />
                Public Website
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Website enabled</label>
                  {isEditMode ? (
                    <select
                      name="website_enabled"
                      value={formik.values.website_enabled ? "1" : "0"}
                      onChange={(e) =>
                        formik.setFieldValue("website_enabled", e.target.value === "1")
                      }
                      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    >
                      <option value="0">No</option>
                      <option value="1">Yes</option>
                    </select>
                  ) : (
                    <p className="text-gray-800 mt-1">
                      {cooperative?.website_enabled ? "Yes" : "No"}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Website type</label>
                  {isEditMode ? (
                    <select
                      name="website_type"
                      value={formik.values.website_type}
                      onChange={formik.handleChange}
                      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    >
                      <option value="NONE">NONE</option>
                      <option value="STANDARD">STANDARD</option>
                      <option value="CUSTOM">CUSTOM</option>
                    </select>
                  ) : (
                    <p className="text-gray-800 mt-1">{cooperative?.website_type || "NONE"}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Website runtime</label>
                  {isEditMode ? (
                    <select
                      name="website_runtime"
                      value={formik.values.website_runtime}
                      onChange={formik.handleChange}
                      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    >
                      <option value="NONE">NONE</option>
                      <option value="STANDARD">STANDARD</option>
                      <option value="CUSTOM_SHARED">CUSTOM_SHARED</option>
                      <option value="DEDICATED_REPO">DEDICATED_REPO</option>
                    </select>
                  ) : (
                    <p className="text-gray-800 mt-1">{cooperative?.website_runtime || "NONE"}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Website key</label>
                  {isEditMode ? (
                    <Input
                      name="website_key"
                      value={formik.values.website_key}
                      onChange={formik.handleChange}
                      placeholder="e.g. sisiayola or default"
                    />
                  ) : (
                    <p className="text-gray-800 mt-1 font-mono">
                      {cooperative?.website_key || "—"}
                    </p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-500">Deploy URL (DEDICATED_REPO)</label>
                  {isEditMode ? (
                    <Input
                      name="website_deploy_url"
                      value={formik.values.website_deploy_url}
                      onChange={formik.handleChange}
                      placeholder="https://example.vercel.app"
                    />
                  ) : (
                    <p className="text-gray-800 mt-1 font-mono text-sm break-all bg-gray-100 px-2 py-1 rounded">
                      {cooperative?.website_deploy_url || "—"}
                    </p>
                  )}
                </div>
              </div>
              {!isEditMode && (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>
                      {cooperative?.website_enabled
                        ? `Public site: ${cooperativeUrl} — member portal at ${cooperativeUrl}/login`
                        : `Member portal: ${cooperativeUrl}/login (enable Public Website so / serves the marketing site)`}
                      {cooperative?.custom_domain_status === "active" &&
                      cooperative?.custom_domain
                        ? ` · Custom domain active (subdomain still live: ${subdomainUrl})`
                        : ""}
                    </p>
                    {cooperative?.custom_domain_status === "active" &&
                      cooperative?.custom_domain && (
                        <p className="text-xs text-[#31778c]">
                          After DNS is ready, click <strong>Publish website routing</strong> so
                          Edge Config includes <span className="font-mono">{cooperative.custom_domain}</span>.
                          Until then, <span className="font-mono">/</span> may redirect to login while{" "}
                          <span className="font-mono">/login</span> still works.
                        </p>
                      )}
                  </div>
                  <button
                    type="button"
                    onClick={handlePublishWebsiteRouting}
                    disabled={isPublishingRouting}
                    className="inline-flex items-center justify-center rounded-md bg-[#31778c] px-4 py-2 text-sm font-medium text-white transition hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
                  >
                    {isPublishingRouting ? "Publishing…" : "Publish website routing"}
                  </button>
                </div>
              )}
            </div>

            <CooperativeModuleAssignmentCard companyId={id} />

            {/* Subscription Information */}
            <div className="md:col-span-2 space-y-4 mt-6 pt-6 border-t">
              <h4 className="text-md font-semibold text-gray-700 mb-4 flex items-center">
                <FaTag className="mr-2 text-[#8B313A]" />
                Subscription Information
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Subscription Plan</label>
                  <p className="text-gray-800 mt-1 font-semibold">
                    {subscription?.plan?.name || "N/A"}
                  </p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500 flex items-center">
                    <FaCalendarAlt className="mr-2 text-gray-400" />
                    Start Date
                  </label>
                  <p className="text-gray-800 mt-1">
                    {subscription?.start_date ? formatDate(subscription.start_date) : 
                     cooperative?.subscription_start_date ? formatDate(cooperative.subscription_start_date) : "N/A"}
                  </p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Duration</label>
                  <p className="text-gray-800 mt-1">
                    {cooperative?.subscription_duration && cooperative?.subscription_duration_type
                      ? `${cooperative.subscription_duration} ${cooperative.subscription_duration_type.toLowerCase()}`
                      : subscription?.duration 
                      ? `${subscription.duration} ${subscription.duration_type?.toLowerCase() || 'months'}`
                      : "N/A"}
                  </p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Expiry Date</label>
                  <p className="text-gray-800 mt-1">
                    {subscription?.expires_at 
                      ? formatDate(subscription.expires_at)
                      : cooperative?.subscription_start_date && cooperative?.subscription_duration && cooperative?.subscription_duration_type
                      ? calculateExpiryDate(
                          cooperative.subscription_start_date,
                          cooperative.subscription_duration,
                          cooperative.subscription_duration_type
                        )
                      : "N/A"}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Member slot capacity</label>
                  <p className="text-gray-800 mt-1">
                    {subscription?.member_slot_capacity != null
                      ? subscription.member_slot_capacity.toLocaleString()
                      : "N/A"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Slots used</label>
                  <p className="text-gray-800 mt-1">
                    {cooperative?.total_members != null
                      ? cooperative.total_members.toLocaleString()
                      : "N/A"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Discount applied</label>
                  <p className="text-gray-800 mt-1">
                    {subscription?.discount_percentage != null && subscription.discount_percentage > 0
                      ? `${subscription.discount_percentage}%`
                      : "0%"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Amount charged</label>
                  <p className="text-gray-800 mt-1">
                    {subscription?.amount_charged != null
                      ? `₦${parseFloat(subscription.amount_charged).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                      : "N/A"}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">SMS Units</label>
                  <p className="text-gray-800 mt-1">
                    {subscription?.available_sms_units?.toLocaleString() || "0"}
                  </p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Email Count</label>
                  <p className="text-gray-800 mt-1">
                    {subscription?.available_email_count?.toLocaleString() || "0"}
                  </p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Days Remaining</label>
                  <p className={`mt-1 font-semibold ${daysLeft <= 5 ? 'text-red-600' : 'text-gray-800'}`}>
                    {daysLeft} days
                  </p>
                </div>
              </div>

              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddSlotsModal(true);
                    setProrateResult(null);
                    setAddSlotsCount(1);
                    setAddSlotsDiscount("");
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#8B313A] hover:bg-[#7a2a32]"
                >
                  Add member slots
                </button>
              </div>
            </div>

            {/* Payment Fee Settings — independent of main cooperative Edit */}
            <div className="md:col-span-2 space-y-4 mt-6 pt-6 border-t">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-2">
                <div>
                  <h4 className="text-md font-semibold text-gray-700">
                    Payment Fee Settings
                  </h4>
                  <p className="text-xs text-gray-500 mt-1 max-w-xl">
                    Member online payments (Paystack): who bears gateway and convenience fees, and optional convenience amount. Edit separately from cooperative profile.
                  </p>
                </div>
                {!isPaymentFeeEditMode ? (
                  <button
                    type="button"
                    onClick={() => setIsPaymentFeeEditMode(true)}
                    className="flex items-center gap-2 px-4 py-2 border border-[#8B313A] text-[#8B313A] rounded-lg hover:bg-[#8B313A]/5 transition-colors shrink-0"
                  >
                    <FaEdit className="text-sm" />
                    <span>Edit payment fees</span>
                  </button>
                ) : (
                  <div className="flex flex-wrap items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={handleCancelPaymentFees}
                      disabled={isUpdating}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
                    >
                      <FaTimes className="text-sm" />
                      <span>Cancel</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleSavePaymentFees}
                      disabled={isUpdating}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      <FaCheck className="text-sm" />
                      <span>{isUpdating ? "Saving…" : "Save fees"}</span>
                    </button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Gateway Fee Bearer</label>
                  {isPaymentFeeEditMode ? (
                    <select
                      name="gateway_fee_bearer"
                      value={formik.values.gateway_fee_bearer}
                      onChange={formik.handleChange}
                      className="w-full mt-1 border border-gray-300 rounded px-3 py-2"
                    >
                      <option value="member">Member</option>
                      <option value="organization">Organization</option>
                    </select>
                  ) : (
                    <p className="text-gray-800 mt-1 capitalize">{cooperative?.gateway_fee_bearer || "member"}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Convenience Fee Bearer</label>
                  {isPaymentFeeEditMode ? (
                    <select
                      name="convenience_fee_bearer"
                      value={formik.values.convenience_fee_bearer}
                      onChange={formik.handleChange}
                      className="w-full mt-1 border border-gray-300 rounded px-3 py-2"
                    >
                      <option value="member">Member</option>
                      <option value="organization">Organization</option>
                    </select>
                  ) : (
                    <p className="text-gray-800 mt-1 capitalize">{cooperative?.convenience_fee_bearer || "member"}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Convenience Fee (NGN)</label>
                  {isPaymentFeeEditMode ? (
                    <>
                      <Input
                        name="convenience_fee_ngn"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formik.values.convenience_fee_ngn}
                        onChange={formik.handleChange}
                        className="mt-1"
                        placeholder="Leave empty for default ₦100; set 0 for no fee"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Empty = default ₦100, 0 = no convenience fee
                      </p>
                    </>
                  ) : (
                    <p className="text-gray-800 mt-1">
                      {cooperative?.convenience_fee_ngn === null || cooperative?.convenience_fee_ngn === undefined
                        ? "Default (₦100)"
                        : `₦${Number(cooperative.convenience_fee_ngn).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                          })}`}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <CooperativePaymentMethodsCard companyId={id} />

            {/* Add member slots modal */}
            <Modal
              show={showAddSlotsModal}
              onHide={() => {
                setShowAddSlotsModal(false);
                setProrateResult(null);
              }}
              size="md"
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>Add member slots</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Number of slots</label>
                    <input
                      type="number"
                      min={1}
                      value={addSlotsCount}
                      onChange={(e) => setAddSlotsCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Discount (%) — optional</label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={0.01}
                      value={addSlotsDiscount}
                      onChange={(e) => setAddSlotsDiscount(e.target.value)}
                      placeholder="0"
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <button
                      type="button"
                      disabled={prorateLoading}
                      onClick={async () => {
                        try {
                          const res = await triggerProrate({
                            companyId: id,
                            new_slot_count: addSlotsCount,
                            discount_percentage: addSlotsDiscount === "" ? undefined : parseFloat(addSlotsDiscount),
                          }).unwrap();
                          setProrateResult(res ?? null);
                        } catch (err) {
                          toast.error(err?.data?.message || "Failed to calculate prorated amount.");
                          setProrateResult(null);
                        }
                      }}
                      className="px-4 py-2 text-white bg-gray-600 rounded hover:bg-gray-700 disabled:opacity-50"
                    >
                      {prorateLoading ? "Calculating…" : "Calculate prorated amount"}
                    </button>
                  </div>
                  {prorateResult && (
                    <div className="rounded-lg bg-gray-50 p-4 space-y-2 text-sm">
                      <p><span className="text-gray-500">Remaining days:</span> {prorateResult.remaining_days}</p>
                      <p><span className="text-gray-500">Price per member:</span> ₦{Number(prorateResult.price_per_member).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                      <p><span className="text-gray-500">Prorated before discount:</span> ₦{Number(prorateResult.prorated_amount_before_discount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                      {prorateResult.discount_percentage > 0 && (
                        <p><span className="text-gray-500">Discount:</span> {prorateResult.discount_percentage}%</p>
                      )}
                      <p className="font-semibold"><span className="text-gray-500">Prorated after discount:</span> ₦{Number(prorateResult.prorated_amount_after_discount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                      <p><span className="text-gray-500">Expiry date (unchanged):</span> {prorateResult.expires_at ? new Date(prorateResult.expires_at).toLocaleDateString() : "—"}</p>
                    </div>
                  )}
                </div>
              </Modal.Body>
              <Modal.Footer>
                <button
                  type="button"
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                  onClick={() => {
                    setShowAddSlotsModal(false);
                    setProrateResult(null);
                  }}
                >
                  Cancel
                </button>
                {prorateResult && (
                  <button
                    type="button"
                    disabled={topUpLoading}
                    className="px-4 py-2 text-white bg-[#8B313A] rounded hover:bg-[#7a2a32] disabled:opacity-50"
                    onClick={async () => {
                      try {
                        await topUpMemberSlots({
                          companyId: id,
                          new_slot_count: addSlotsCount,
                          discount_percentage: addSlotsDiscount === "" ? 0 : parseFloat(addSlotsDiscount),
                        }).unwrap();
                        toast.success("Member slots added successfully.");
                        setShowAddSlotsModal(false);
                        setProrateResult(null);
                        refetchCooperative();
                      } catch (err) {
                        toast.error(err?.data?.message || "Failed to add slots.");
                      }
                    }}
                  >
                    {topUpLoading ? "Adding…" : "Confirm and add slots"}
                  </button>
                )}
              </Modal.Footer>
            </Modal>

            {/* Registration Information */}
            <div className="md:col-span-2 space-y-4 mt-6 pt-6 border-t">
              <h4 className="text-md font-semibold text-gray-700 mb-4">Registration Information</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Registered By</label>
                  <p className="text-gray-800 mt-1">
                    {cooperative?.admin?.name || cooperative?.registered_by?.name || "System Admin"}
                  </p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Registration Date</label>
                  <p className="text-gray-800 mt-1">
                    {cooperative?.created_at ? formatDate(cooperative.created_at) : "N/A"}
                  </p>
                </div>
              </div>
            </div>
          </div>
          </form>
        </div>
      </CardComp>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return renderOverview();
      case "details":
        return renderDetails();
      case "loans":
        return renderLoans();
      case "contributions":
        return renderContributions();
      case "transactions":
        return renderTransactions();
      case "statistics":
        return renderStatistics();
      default:
        return renderOverview();
    }
  };

  return (
    <div className="min-h-screen p-4 md:p-6 bg-gray-50">
      {/* Header */}
      <div className="bg-white rounded-xl p-4 md:p-6 mb-4 md:mb-6 shadow-sm border border-gray-100">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-3 md:px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300 w-fit"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              <span className="hidden sm:inline">Back</span>
            </button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-[#45464e]">
                {cooperativeData?.data?.name || "Cooperative Details"}
              </h1>
              {(cooperativeData?.data?.public_url ||
                cooperativeData?.data?.domain_url) && (
                <p className="text-sm md:text-base text-gray-600 break-all">
                  {cooperativeData.data.public_url ||
                    `${cooperativeData.data.domain_url}.uniquecoop.net`}
                </p>
              )}
            </div>
          </div>

          {/* Branch Filter Dropdown */}
          <div className="w-full sm:w-64">
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Filter by Branch
            </label>
            <CustomSelect
              options={[
                { value: null, label: "All Branches" },
                ...(Array.isArray(cooperativeData?.data?.branches)
                  ? cooperativeData.data.branches.map((branch) => ({
                      value: branch.id,
                      label: branch.name,
                    }))
                  : []),
              ]}
              value={selectedBranch || { value: null, label: "All Branches" }}
              onChange={(option) => setSelectedBranch(option)}
              placeholder="Select Branch"
              styles={customStyles}
              isSearchable={true}
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl p-4 md:p-6 mb-4 md:mb-6 shadow-sm border border-gray-100">
        <div className="flex flex-wrap gap-1 bg-gray-100 p-1 rounded-lg">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 min-w-0 flex items-center justify-center gap-1 md:gap-2 px-2 md:px-4 py-2 md:py-3 rounded-md text-xs md:text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? "bg-white text-[#8B313A] shadow-sm"
                  : "text-gray-600 hover:text-gray-800"
              }`}
            >
              <span className="text-sm md:text-lg">{tab.icon}</span>
              <span className="truncate">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {renderTabContent()}

      {/* Transaction details */}
      <Modal
        show={Boolean(selectedTransaction)}
        onHide={() => setSelectedTransaction(null)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-[#8b313a] font-semibold">
            Transaction Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTransaction ? (
            (() => {
              const tx = selectedTransaction;
              const signed = Number(tx.signed_amount ?? tx.amount ?? 0);
              const amountColor =
                signed < 0 ? "#d40000" : signed > 0 ? "#19BC31" : "#383838";
              const statusUpper = String(tx.status || "").toUpperCase();
              const statusBg =
                ["SUCCESSFUL", "SUCCESS", "PROCESSED", "PAID"].includes(statusUpper)
                  ? "#19BC31"
                  : statusUpper === "PENDING"
                  ? "#FFA500"
                  : "#FF4343";
              const memberName = tx.member
                ? `${tx.member.first_name || ""} ${tx.member.last_name || ""}`.trim()
                : "—";
              const memberNo = tx.member_no || tx.member?.member_no || "—";
              const typeLabel = tx.display_type || tx.type || "—";
              const paymentLabel =
                tx.display_payment_method || tx.payment_method || "—";
              const recordedByName =
                tx.recorded_by?.name ||
                (tx.recorded_by
                  ? `${tx.recorded_by.first_name || ""} ${
                      tx.recorded_by.last_name || ""
                    }`.trim()
                  : null);
              const bank = tx.company_bank;
              const receipts = Array.isArray(tx.receipt_paths)
                ? tx.receipt_paths.filter(Boolean)
                : [];

              return (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <DetailField label="Member">{memberName || "—"}</DetailField>
                    <DetailField label="Membership Number">{memberNo}</DetailField>
                    <DetailField label="Transaction Type">{typeLabel}</DetailField>
                    <DetailField label="Amount">
                      <span style={{ color: amountColor, fontSize: "1.1rem" }}>
                        {formatMoney(Math.abs(signed).toFixed(2), true)}
                      </span>
                      {signed !== 0 ? (
                        <p className="text-xs font-normal text-gray-500 mt-1 mb-0">
                          {signed < 0 ? "Money out (debit)" : "Money in (credit)"}
                        </p>
                      ) : null}
                    </DetailField>
                    <DetailField label="Reference">
                      <span className="font-mono text-xs">{tx.reference || "—"}</span>
                    </DetailField>
                    <DetailField label="Payment Method">{paymentLabel}</DetailField>
                    <DetailField label="Status">
                      <span
                        className="inline-block px-3 py-1 rounded-full text-xs font-medium text-white"
                        style={{ backgroundColor: statusBg }}
                      >
                        {tx.status || "—"}
                      </span>
                    </DetailField>
                    <DetailField label="Date & Time">
                      {formatListDateTime(tx.created_at)}
                    </DetailField>
                    {tx.action ? (
                      <DetailField label="Action">{tx.action}</DetailField>
                    ) : null}
                    {recordedByName ? (
                      <DetailField label="Recorded by">{recordedByName}</DetailField>
                    ) : null}
                    {tx.remark ? (
                      <DetailField label="Remark / Description" full>
                        {tx.remark}
                      </DetailField>
                    ) : null}
                  </div>

                  {bank && typeof bank === "object" ? (
                    <div className="rounded-lg border border-gray-200 bg-[#fafafa] p-4">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                        Society bank account
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">Bank</p>
                          <p className="font-medium text-[#383838] mb-0">
                            {bank.bank_name || bank.name || "—"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">Account name</p>
                          <p className="font-medium text-[#383838] mb-0">
                            {bank.account_name || "—"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">Account number</p>
                          <p className="font-medium text-[#383838] mb-0 font-mono">
                            {bank.account_number || "—"}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {tx.member_note ? (
                    <div className="rounded-lg border border-gray-200 p-4">
                      <p className="text-xs text-gray-500 mb-1">Member note</p>
                      <p className="text-sm text-[#383838] mb-0">{tx.member_note}</p>
                    </div>
                  ) : null}

                  {receipts.length > 0 ? (
                    <div>
                      <p className="text-xs text-gray-500 mb-2">Receipts</p>
                      <ul className="space-y-1 list-disc list-inside">
                        {receipts.map((path, idx) => (
                          <li key={`${path}-${idx}`} className="text-sm">
                            <a
                              href={path}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#31778c] hover:underline break-all"
                            >
                              View receipt {receipts.length > 1 ? idx + 1 : ""}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  <div className="flex justify-end pt-2">
                    <button
                      type="button"
                      onClick={() => setSelectedTransaction(null)}
                      className="px-5 py-2 text-sm font-medium text-white rounded-md bg-[#8b313a] hover:bg-[#6b252a]"
                    >
                      Close
                    </button>
                  </div>
                </div>
              );
            })()
          ) : null}
        </Modal.Body>
      </Modal>

      {/* Confirmation Modal */}
      <ModalCompAdvance
        show={showConfirmModal}
        handleClose={handleCancelConfirm}
        modalTitle=""
        modalBody={0}
        size="sm"
        centered={true}
        arrayComp={[
          {
            name: "confirm",
            component: (
              <Message
                setModalToggle={setShowConfirmModal}
                title={
                  confirmAction === "credentials"
                    ? "Send Login Credentials"
                    : "Resend Welcome Email"
                }
                desc={
                  confirmAction === "credentials"
                    ? "Are you sure you want to send login credentials to this cooperative? This will email them their default login details including domain and password."
                    : "Are you sure you want to resend the welcome email to this cooperative?"
                }
                onBack={handleCancelConfirm}
                onProceed={handleConfirmAction}
                loading={isSendingAction}
              />
            ),
          },
        ]}
      />
    </div>
  );
};

export default CooperativeDetailsPage;
